/**
 * @module store
 *
 * Imports the `rootReducer` and exports a Redux store.
 */
import { configureStore } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import { enableReduxDevMiddleware, enableReduxLog, env, logLevel } from '~/config/settings/app.settings';
import { tokenExpiredMiddleware } from './middlewares';
import rootReducer from './reducers';

/**
 * Take the Log Adapter's log-level and map it to supported Redux Logger levels.
 *
 * @see `/src/adapters/log`
 * @see `/src/config/settings`
 * @link https://www.npmjs.com/package/redux-logger#options
 */
const normalizeLogLevel = (level) => {
  switch (level) {
    case 'trace':
    case 'debug':
    case 'info':
      return 'info';
    case 'warn':
      return 'warn';
    case 'error':
    case 'fatal':
      return 'error';
    default:
      return 'log';
  }
};

/**
 * Create custom redux-logger for logging actions during development.
 */
const reduxLogger = createLogger({
  level: normalizeLogLevel(logLevel),
  collapsed: true,
  duration: env !== 'production',
});

/**
 * Return an array of middlewares to be used with redux-toolkit `configureStore()`.
 */
const combineMiddlewares = (getDefaultMiddleware) => {
  // Don't load dev middleware unless explicitly enabled (can be really slow
  // on some pages with lots of data).
  const middlewares = getDefaultMiddleware({
    immutableStateInvariant: enableReduxDevMiddleware,
    serializableCheck: enableReduxDevMiddleware,
  });

  if (enableReduxLog) middlewares.push(reduxLogger);

  middlewares.push(tokenExpiredMiddleware);

  return middlewares;
};

/**
 * Create store from app's reducers combined with middlewares.
 */
const store = configureStore({
  reducer: rootReducer,
  middleware: combineMiddlewares,
  devTools: env !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
export { store, rootReducer, combineMiddlewares };
